import React, { useEffect, useMemo, useState, useCallback } from 'react';
import styled from 'styled-components';
import Swal from 'sweetalert2';
import AdminSalesLayout from '../../components/AdminSalesLayout';
import {
  listCourseCodes,
  createCourseCode,
  updateCourseCode,
  deleteCourseCode,
  listExtraCodes,
  createExtraCode,
  updateExtraCode,
  deleteExtraCode,
} from '../../services/courseCodeService';

const PageSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  max-width: 1200px;
`;

const SectionCard = styled.section`
  background: #ffffff;
  border-radius: 10px;
  padding: 24px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04);
`;

const SectionTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #111827;
  margin: 0 0 16px;
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 16px;
`;

const SummaryCard = styled.div`
  border: 1px solid #e5e7eb;
  border-left: 4px solid #10b981;
  border-radius: 8px;
  padding: 16px 20px;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  gap: 6px;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
  }
`;

const SummaryLabel = styled.span`
  font-size: 12px;
  font-weight: 700;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.6px;
`;

const SummaryValue = styled.span`
  font-size: 28px;
  font-weight: 800;
  color: #111827;
`;

const SummarySubtitle = styled.span`
  font-size: 12px;
  color: #6b7280;
`;

const Toolbar = styled.form`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
  margin-bottom: 16px;
`;

const SearchInput = styled.input`
  flex: 1;
  min-width: 220px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 14px;
  background: #ffffff;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  &:focus {
    border-color: #10b981;
    box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.15);
    outline: none;
  }
`;

const PrimaryButton = styled.button`
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: #ffffff;
  border: none;
  border-radius: 8px;
  padding: 10px 16px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  box-shadow: 0 2px 6px rgba(5, 150, 105, 0.25);
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(5, 150, 105, 0.3);
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    box-shadow: none;
  }
`;

const SecondaryButton = styled.button`
  border: 1px solid #cbd5e1;
  background: #ffffff;
  color: #334155;
  border-radius: 8px;
  padding: 10px 16px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease;
  &:hover {
    background: #f3f4f6;
    border-color: #9ca3af;
  }
`;

const DangerButton = styled.button`
  border: 1px solid #fecaca;
  background: #fef2f2;
  color: #b91c1c;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease;
  &:hover {
    background: #fee2e2;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const AccentButton = styled.button`
  border: 1px solid #a7f3d0;
  background: #ecfdf5;
  color: #065f46;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease;
  white-space: nowrap;
  &:hover {
    background: #d1fae5;
    border-color: #6ee7b7;
  }
`;

const TableWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 720px;
`;

const Th = styled.th`
  text-align: left;
  font-size: 12px;
  font-weight: 700;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  padding: 12px 16px;
  background: #f9fafb;
  border-bottom: 2px solid #e5e7eb;
`;

const Td = styled.td`
  padding: 14px 16px;
  border-bottom: 1px solid #e5e7eb;
  font-size: 14px;
  color: #374151;
  vertical-align: middle;
  background: #ffffff;
`;

const Tr = styled.tr`
  transition: background-color 0.15s ease;
  &:nth-child(even) ${Td} {
    background: #f9fafb;
  }
  &:hover ${Td} {
    background: #f3f4f6;
  }
`;

const SortableHeader = styled.button`
  border: none;
  background: transparent;
  padding: 0;
  margin: 0;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 700;
  color: ${(p) => (p.$active ? '#059669' : '#6b7280')};
  text-transform: uppercase;
  letter-spacing: 0.6px;
  cursor: pointer;
`;

const SortIcon = styled.span`
  display: inline-flex;
  width: 12px;
  height: 12px;
  position: relative;
  opacity: ${(p) => (p.$visible ? 1 : 0.3)};

  &::before,
  &::after {
    content: '';
    position: absolute;
    left: 50%;
    width: 0;
    height: 0;
    border-left: 4px solid transparent;
    border-right: 4px solid transparent;
  }

  &::before {
    top: 0;
    border-bottom: 5px solid ${(p) => (p.$direction === 'asc' ? '#059669' : '#9ca3af')};
  }

  &::after {
    bottom: 0;
    border-top: 5px solid ${(p) => (p.$direction === 'desc' ? '#059669' : '#9ca3af')};
  }
`;

const UsageBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  background: ${(p) => (p.$highlight ? '#ecfdf5' : '#f3f4f6')};
  color: ${(p) => (p.$highlight ? '#065f46' : '#6b7280')};
  border: 1px solid ${(p) => (p.$highlight ? '#a7f3d0' : '#e5e7eb')};
`;

const Label = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 6px;
`;

const Input = styled.input`
  width: 100%;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 14px;
  background: #f8fafc;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  &:focus {
    border-color: #10b981;
    box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.15);
    outline: none;
  }
`;

const FieldError = styled.span`
  font-size: 12px;
  color: #dc2626;
  margin-top: 4px;
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(17, 24, 39, 0.45);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 40px 16px;
  z-index: 1100;
  overflow-y: auto;
`;

const ModalCard = styled.div`
  width: 100%;
  max-width: 560px;
  background: #ffffff;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 20px 40px -8px rgba(17, 24, 39, 0.2);
  border: 1px solid #e5e7eb;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const ModalTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #111827;
  margin: 0;
`;

const CloseButton = styled.button`
  border: none;
  background: transparent;
  color: #94a3b8;
  font-size: 22px;
  cursor: pointer;
  &:hover {
    color: #1e293b;
  }
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 0;
  color: #9ca3af;
  font-size: 14px;
`;

const ErrorBanner = styled.div`
  border: 1px solid #fecaca;
  background: #fef2f2;
  color: #991b1b;
  border-radius: 8px;
  padding: 14px 18px;
  font-size: 14px;
`;

const Pagination = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  padding-top: 16px;
`;

const PaginationGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PaginationButton = styled.button`
  border: 1px solid #cbd5e1;
  background: #ffffff;
  color: #059669;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease;
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
  &:hover:not(:disabled) {
    background: #ecfdf5;
    border-color: #a7f3d0;
  }
`;

const Select = styled.select`
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 13px;
  background: #ffffff;
  color: #111827;
`;

const MetaText = styled.span`
  font-size: 13px;
  color: #6b7280;
`;

// ─── Extra Codes Modal ────────────────────────────────────────────────────────

function ExtraCodesModal({ record, onClose }) {
  const [items, setItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(true);
  const [addInput, setAddInput] = useState('');
  const [addError, setAddError] = useState('');
  const [addSubmitting, setAddSubmitting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editInput, setEditInput] = useState('');
  const [editError, setEditError] = useState('');
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [pendingDeleteItem, setPendingDeleteItem] = useState(null);
  const [deleteError, setDeleteError] = useState('');

  const loadItems = useCallback(async () => {
    try {
      setLoadingItems(true);
      const response = await listExtraCodes(record.id);
      setItems(response?.data || []);
    } catch {
      setItems([]);
    } finally {
      setLoadingItems(false);
    }
  }, [record.id]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const handleAdd = async (e) => {
    e.preventDefault();
    const name = addInput.trim();
    if (!name) { setAddError('Code name is required.'); return; }
    try {
      setAddSubmitting(true);
      setAddError('');
      await createExtraCode(record.id, { otherCodeName: name });
      setAddInput('');
      loadItems();
    } catch (err) {
      const msg = err?.response?.data?.errors?.otherCodeName
        || err?.response?.data?.message
        || 'Failed to add.';
      setAddError(msg);
    } finally {
      setAddSubmitting(false);
    }
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setEditInput(item.otherCodeName);
    setEditError('');
    setPendingDeleteItem(null);
    setDeleteError('');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditInput('');
    setEditError('');
  };

  const requestDelete = (item) => {
    setPendingDeleteItem(item);
    setDeleteError('');
    if (editingId === item.id) {
      cancelEdit();
    }
  };

  const cancelDelete = () => {
    if (deletingId) {
      return;
    }
    setPendingDeleteItem(null);
    setDeleteError('');
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    const name = editInput.trim();
    if (!name) { setEditError('Code name is required.'); return; }
    try {
      setEditSubmitting(true);
      setEditError('');
      await updateExtraCode(record.id, editingId, { otherCodeName: name });
      setEditingId(null);
      loadItems();
    } catch (err) {
      const msg = err?.response?.data?.errors?.otherCodeName
        || err?.response?.data?.message
        || 'Failed to update.';
      setEditError(msg);
    } finally {
      setEditSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    if (!pendingDeleteItem) {
      return;
    }

    try {
      setDeletingId(pendingDeleteItem.id);
      setDeleteError('');
      await deleteExtraCode(record.id, pendingDeleteItem.id);
      setPendingDeleteItem(null);
      loadItems();
    } catch (err) {
      setDeleteError(err?.response?.data?.message || 'Failed to remove extra code.');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <ModalOverlay>
      <ModalCard style={{ maxWidth: 600 }}>
        <ModalHeader>
          <div>
            <ModalTitle>Extra Codes</ModalTitle>
            <div style={{ fontSize: 13, color: '#6b7280', marginTop: 2 }}>
              {record.fullName}
              {record.shortName && (
                <span style={{ marginLeft: 6, fontWeight: 600, color: '#065f46' }}>
                  · {record.shortName}
                </span>
              )}
            </div>
          </div>
          <CloseButton type="button" onClick={onClose}>&times;</CloseButton>
        </ModalHeader>

        {/* Divider */}
        <div style={{ height: 1, background: '#e2e8f0', marginBottom: 18 }} />

        {/* Add new code */}
        <div style={{ marginBottom: 20 }}>
          <Label style={{ marginBottom: 8, display: 'block' }}>
            Add a new extra code
          </Label>
          <form onSubmit={handleAdd} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
            <div style={{ flex: 1 }}>
              <Input
                type="text"
                placeholder="Enter extra code name..."
                value={addInput}
                onChange={(e) => setAddInput(e.target.value)}
                disabled={addSubmitting}
              />
              {addError && <FieldError style={{ display: 'block', marginTop: 4 }}>{addError}</FieldError>}
            </div>
            <PrimaryButton type="submit" disabled={addSubmitting} style={{ whiteSpace: 'nowrap' }}>
              {addSubmitting ? 'Adding...' : '+ Add'}
            </PrimaryButton>
          </form>
        </div>

        {/* List */}
        <div style={{ marginBottom: 20 }}>
          <Label style={{ marginBottom: 10, display: 'block' }}>
            Existing extra codes
            {!loadingItems && (
              <span style={{ fontWeight: 400, color: '#9ca3af', marginLeft: 6 }}>
                ({items.length})
              </span>
            )}
          </Label>
          {loadingItems ? (
            <EmptyState>Loading...</EmptyState>
          ) : items.length === 0 ? (
            <div style={{
              padding: '24px 16px',
              background: '#f9fafb',
              border: '1px dashed #d1d5db',
              borderRadius: 8,
              textAlign: 'center',
              color: '#9ca3af',
              fontSize: 14,
            }}>
              No extra codes yet. Add one above.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {items.map((item) => (
                <div
                  key={item.id}
                  style={{
                    border: '1px solid #e5e7eb',
                    borderRadius: 8,
                    padding: '10px 14px',
                    background: editingId === item.id ? '#f9fafb' : '#ffffff',
                    transition: 'background 0.15s',
                  }}
                >
                  {editingId === item.id ? (
                    <form onSubmit={handleUpdate} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                      <div style={{ flex: 1 }}>
                        <Input
                          type="text"
                          value={editInput}
                          onChange={(e) => setEditInput(e.target.value)}
                          disabled={editSubmitting}
                          // eslint-disable-next-line jsx-a11y/no-autofocus
                          autoFocus
                        />
                        {editError && <FieldError style={{ display: 'block', marginTop: 4 }}>{editError}</FieldError>}
                      </div>
                      <PrimaryButton
                        type="submit"
                        disabled={editSubmitting}
                        style={{ padding: '8px 12px', fontSize: 13, whiteSpace: 'nowrap' }}
                      >
                        {editSubmitting ? 'Saving...' : 'Save'}
                      </PrimaryButton>
                      <SecondaryButton
                        type="button"
                        onClick={cancelEdit}
                        disabled={editSubmitting}
                        style={{ padding: '8px 12px', fontSize: 13 }}
                      >
                        Cancel
                      </SecondaryButton>
                    </form>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                      <span style={{ fontWeight: 500, color: '#111827', fontSize: 14 }}>
                        {item.otherCodeName}
                      </span>
                      <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                        <SecondaryButton
                          type="button"
                          onClick={() => startEdit(item)}
                          style={{ padding: '5px 10px', fontSize: 12 }}
                        >
                          Edit
                        </SecondaryButton>
                        <DangerButton
                          type="button"
                          onClick={() => requestDelete(item)}
                          disabled={deletingId === item.id}
                          style={{ padding: '5px 10px', fontSize: 12 }}
                        >
                          {deletingId === item.id ? '...' : 'Remove'}
                        </DangerButton>
                      </div>
                    </div>
                  )}

                  {pendingDeleteItem?.id === item.id && (
                    <div
                      style={{
                        marginTop: 10,
                        padding: '12px 14px',
                        borderRadius: 10,
                        background: '#fff7ed',
                        border: '1px solid #fdba74',
                      }}
                    >
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#9a3412', marginBottom: 6 }}>
                        Remove this extra code?
                      </div>
                      <div style={{ fontSize: 13, color: '#7c2d12', marginBottom: 10 }}>
                        This will remove <strong>{item.otherCodeName}</strong> from <strong>{record.shortName || record.fullName}</strong>.
                      </div>
                      {deleteError && (
                        <div style={{ fontSize: 12, color: '#b91c1c', marginBottom: 10 }}>
                          {deleteError}
                        </div>
                      )}
                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                        <SecondaryButton
                          type="button"
                          onClick={cancelDelete}
                          disabled={deletingId === item.id}
                          style={{ padding: '6px 10px', fontSize: 12 }}
                        >
                          Keep it
                        </SecondaryButton>
                        <DangerButton
                          type="button"
                          onClick={confirmDelete}
                          disabled={deletingId === item.id}
                          style={{ padding: '6px 10px', fontSize: 12 }}
                        >
                          {deletingId === item.id ? 'Removing...' : 'Yes, remove'}
                        </DangerButton>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: 4 }}>
          <SecondaryButton type="button" onClick={onClose}>Close</SecondaryButton>
        </div>
      </ModalCard>
    </ModalOverlay>
  );
}

// ──────────────────────────────────────────────────────────────────────────────

function useCourseCodeForm(initialState) {
  const [formData, setFormData] = useState(initialState);
  const [errors, setErrors] = useState({});

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  return {
    formData,
    setFormData,
    errors,
    setErrors,
    handleChange,
  };
}

const defaultQuery = {
  page: 1,
  pageSize: 10,
  search: '',
  sortBy: 'name',
  sortDir: 'asc',
};

export default function AdminSalesCourseCode() {
  const [query, setQuery] = useState(defaultQuery);
  const [searchInput, setSearchInput] = useState('');
  const [records, setRecords] = useState([]);
  const [meta, setMeta] = useState({ total: 0, totalPages: 1 });
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [activeRecord, setActiveRecord] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [extraCodesRecord, setExtraCodesRecord] = useState(null);

  const form = useCourseCodeForm({ fullName: '', shortName: '' });

  useEffect(() => {
    let ignore = false;
    async function loadData() {
      try {
        setLoading(true);
        setError('');
        const response = await listCourseCodes(query);
        if (ignore) return;
        setRecords(response?.data || []);
        setMeta(response?.meta || { total: 0, totalPages: 1, page: 1, pageSize: query.pageSize });
        setSummary(response?.summary || null);
      } catch (err) {
        if (ignore) return;
        const message = err?.response?.data?.message || 'Failed to load course codes.';
        setError(message);
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    }

    loadData();

    return () => {
      ignore = true;
    };
  }, [query, reloadKey]);

  const totalPages = Math.max(1, meta?.totalPages || 1);

  const summaryItems = useMemo(() => {
    if (!summary) {
      return [];
    }
    return [
      {
        label: 'Total Course Codes',
        value: summary.totalCodes,
        subtitle: 'All records in catalog',
      },
      {
        label: 'Mapped Codes',
        value: summary.mappedCodes,
        subtitle: 'Linked to staff targets',
      },
      {
        label: 'Unmapped Codes',
        value: summary.unmappedCodes,
        subtitle: 'Available for assignment',
      },
      {
        label: 'Total Mappings',
        value: summary.totalMappings,
        subtitle: 'Staff-course assignments',
      },
    ];
  }, [summary]);

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    setQuery((prev) => ({
      ...prev,
      page: 1,
      search: searchInput.trim(),
    }));
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setQuery((prev) => ({
      ...prev,
      page: 1,
      search: '',
    }));
  };

  const handleSort = (key) => {
    setQuery((prev) => ({
      ...prev,
      page: 1,
      sortBy: key,
      sortDir: prev.sortBy === key && prev.sortDir === 'asc' ? 'desc' : 'asc',
    }));
  };

  const handlePageChange = (page) => {
    setQuery((prev) => ({
      ...prev,
      page,
    }));
  };

  const handlePageSizeChange = (event) => {
    const size = Number(event.target.value) || 10;
    setQuery((prev) => ({
      ...prev,
      page: 1,
      pageSize: size,
    }));
  };

  const openCreateModal = () => {
    setActiveRecord(null);
    form.setFormData({ fullName: '', shortName: '' });
    form.setErrors({});
    setModalOpen(true);
  };

  const openEditModal = (record) => {
    setActiveRecord(record);
    form.setFormData({
      fullName: record.fullName || '',
      shortName: record.shortName || '',
    });
    form.setErrors({});
    setModalOpen(true);
  };

  const closeModal = () => {
    if (submitting) return;
    setModalOpen(false);
  };

  const openExtraCodes = (record) => {
    setExtraCodesRecord(record);
  };

  const closeExtraCodes = () => {
    setExtraCodesRecord(null);
  };

  const handleDelete = async (record) => {
    const result = await Swal.fire({
      title: 'Delete course code?',
      text: `This will permanently remove ${record.fullName}.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Delete',
      confirmButtonColor: '#dc2626',
      cancelButtonText: 'Cancel',
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      await deleteCourseCode(record.id);
      Swal.fire({
        title: 'Deleted',
        text: 'Course code deleted successfully.',
        icon: 'success',
        timer: 1600,
        showConfirmButton: false,
      });
      setReloadKey((key) => key + 1);
    } catch (err) {
      const message = err?.response?.data?.message || 'Unable to delete course code.';
      Swal.fire({
        title: 'Unable to delete',
        text: message,
        icon: 'error',
      });
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitting) return;

    const payload = {
      fullName: form.formData.fullName.trim(),
      shortName: form.formData.shortName.trim(),
    };

    if (!payload.fullName) {
      form.setErrors({ fullName: 'Full name is required.' });
      return;
    }
    if (!payload.shortName) {
      form.setErrors({ shortName: 'Short name is required.' });
      return;
    }

    try {
      setSubmitting(true);
      form.setErrors({});

      if (activeRecord) {
        await updateCourseCode(activeRecord.id, payload);
        Swal.fire({
          title: 'Updated',
          text: 'Course code updated successfully.',
          icon: 'success',
          timer: 1600,
          showConfirmButton: false,
        });
      } else {
        await createCourseCode(payload);
        Swal.fire({
          title: 'Created',
          text: 'Course code created successfully.',
          icon: 'success',
          timer: 1600,
          showConfirmButton: false,
        });
      }

      setModalOpen(false);
      setReloadKey((key) => key + 1);
    } catch (err) {
      const responseErrors = err?.response?.data?.errors || {};
      form.setErrors(responseErrors);
      const message = err?.response?.data?.message || 'Unable to save course code.';
      if (!Object.keys(responseErrors).length) {
        Swal.fire({ title: 'Error', text: message, icon: 'error' });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const renderSortIcon = (key) => (
    <SortIcon $visible={query.sortBy === key} $direction={query.sortDir} />
  );

  return (
    <AdminSalesLayout title="Course Codes" currentSection="courseCode">
      <PageSection>
        {error && <ErrorBanner>{error}</ErrorBanner>}

        <SectionCard>
          <SectionTitle>Overview</SectionTitle>
          {summaryItems.length === 0 ? (
            <EmptyState>Loading summary...</EmptyState>
          ) : (
            <SummaryGrid>
              {summaryItems.map((item) => (
                <SummaryCard key={item.label}>
                  <SummaryLabel>{item.label}</SummaryLabel>
                  <SummaryValue>{item.value ?? 0}</SummaryValue>
                  <SummarySubtitle>{item.subtitle}</SummarySubtitle>
                </SummaryCard>
              ))}
            </SummaryGrid>
          )}
        </SectionCard>

        <SectionCard>
          <SectionTitle>Course Code Registry</SectionTitle>
          <Toolbar onSubmit={handleSearchSubmit}>
            <SearchInput
              type="search"
              placeholder="Search by full name or short name"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
            />
            <PrimaryButton type="submit">Search</PrimaryButton>
            <SecondaryButton type="button" onClick={handleClearSearch}>Clear</SecondaryButton>
            <PrimaryButton type="button" onClick={openCreateModal}>
              + New Course Code
            </PrimaryButton>
          </Toolbar>

          <TableWrapper>
            <StyledTable>
              <thead>
                <tr>
                  <Th>
                    <SortableHeader type="button" onClick={() => handleSort('name')} $active={query.sortBy === 'name'}>
                      Full Name
                      {renderSortIcon('name')}
                    </SortableHeader>
                  </Th>
                  <Th>
                    <SortableHeader type="button" onClick={() => handleSort('code')} $active={query.sortBy === 'code'}>
                      Short Name
                      {renderSortIcon('code')}
                    </SortableHeader>
                  </Th>
                  <Th>Actions</Th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <Tr>
                    <Td colSpan={3}>
                      <EmptyState>Loading course codes...</EmptyState>
                    </Td>
                  </Tr>
                ) : records.length === 0 ? (
                  <Tr>
                    <Td colSpan={3}>
                      <EmptyState>No course codes found for the selected filters.</EmptyState>
                    </Td>
                  </Tr>
                ) : (
                  records.map((record) => (
                    <Tr key={record.id}>
                      <Td>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                          <span style={{ fontWeight: 600, color: '#111827' }}>{record.fullName}</span>
                          <MetaText>ID #{record.id}</MetaText>
                        </div>
                      </Td>
                      <Td>
                        <UsageBadge $highlight>{record.shortName || '—'}</UsageBadge>
                      </Td>
                      <Td>
                        <PaginationGroup>
                          <AccentButton type="button" onClick={() => openExtraCodes(record)}>
                            Extra Codes
                          </AccentButton>
                          <SecondaryButton type="button" onClick={() => openEditModal(record)}>
                            Edit
                          </SecondaryButton>
                          <DangerButton type="button" onClick={() => handleDelete(record)}>
                            Delete
                          </DangerButton>
                        </PaginationGroup>
                      </Td>
                    </Tr>
                  ))
                )}
              </tbody>
            </StyledTable>
          </TableWrapper>

          <Pagination>
            <PaginationGroup>
              <PaginationButton
                type="button"
                onClick={() => handlePageChange(Math.max(1, query.page - 1))}
                disabled={query.page <= 1 || loading}
              >
                Previous
              </PaginationButton>
              <MetaText>
                Page {query.page} of {totalPages}
              </MetaText>
              <PaginationButton
                type="button"
                onClick={() => handlePageChange(Math.min(totalPages, query.page + 1))}
                disabled={query.page >= totalPages || loading}
              >
                Next
              </PaginationButton>
            </PaginationGroup>
            <PaginationGroup>
              <Label htmlFor="page-size" style={{ marginBottom: 0 }}>
                Rows per page
              </Label>
              <Select id="page-size" value={query.pageSize} onChange={handlePageSizeChange}>
                {[10, 25, 50, 100].map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </Select>
              <MetaText>{meta.total || 0} total records</MetaText>
            </PaginationGroup>
          </Pagination>
        </SectionCard>
      </PageSection>

      {modalOpen && (
        <ModalOverlay>
          <ModalCard>
            <ModalHeader>
              <ModalTitle>{activeRecord ? 'Edit Course Code' : 'New Course Code'}</ModalTitle>
              <CloseButton type="button" onClick={closeModal}>&times;</CloseButton>
            </ModalHeader>
            <form onSubmit={handleSubmit}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '16px' }}>
                <Label htmlFor="fullName">Full name</Label>
                <Input
                  id="fullName"
                  name="fullName"
                  type="text"
                  placeholder="Example: Higher National Diploma in Computing"
                  value={form.formData.fullName}
                  onChange={form.handleChange}
                />
                {form.errors.fullName && <FieldError>{form.errors.fullName}</FieldError>}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <Label htmlFor="shortName">Short name / code</Label>
                <Input
                  id="shortName"
                  name="shortName"
                  type="text"
                  placeholder="Example: HNDIT"
                  value={form.formData.shortName}
                  onChange={form.handleChange}
                />
                {form.errors.shortName && <FieldError>{form.errors.shortName}</FieldError>}
              </div>
              <ModalActions>
                <SecondaryButton type="button" onClick={closeModal} disabled={submitting}>
                  Cancel
                </SecondaryButton>
                <PrimaryButton type="submit" disabled={submitting}>
                  {submitting ? 'Saving...' : 'Save Changes'}
                </PrimaryButton>
              </ModalActions>
            </form>
          </ModalCard>
        </ModalOverlay>
      )}
      {extraCodesRecord && (
        <ExtraCodesModal record={extraCodesRecord} onClose={closeExtraCodes} />
      )}
    </AdminSalesLayout>
  );
}
